import React, { useState, useEffect, useLayoutEffect } from 'react';
import axios from 'axios';
import SideMenu from '../../../Components/SideMenu';
import Header from '../../../Components/Header';
import Loading from '../../../Components/LoaderScreen';
import Alert from '../../../Components/Alert';
import Modal from '../../../Components/Modal';
import Input from '../../../Components/Input';
import RedirectScreen from '../../../Components/RedirectScreen';

// Import icons
import iconExport from '../../../resources/img/icon-export.svg';
import iconImport from '../../../resources/img/icon-import.svg';
import iconNewCharge from '../../../resources/img/icon-new-charge.svg';
import ApiService from '../../../Utils/services';
import Funcoes from '../../../Utils/functions';
import { validateCPF, validateEmail, formatCPFCNPJ, formatPhone, validateCNPJ, formatCPF } from '../../../Utils/validations';
import { dataBrMask } from '../../../Utils/mask';
import NoResults from '../../../Components/NoResults';
import Pagination from '../../../Components/Pagination';

import iconLixeira from '../../../resources/img/icon-lixeira.svg';
import iconVisualizar from '../../../resources/img/icon-visualizar.svg';
import iconCopy from '../../../resources/img/icon-copy.svg';
import iconTableVisualizar from '../../../resources/img/icon-table-visualizar.svg';

// Estilos definidos como objetos JavaScript para uso inline
const styles = {
    statusBadge: {
        display: 'inline-block',
        padding: '3px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold',
        textAlign: 'center'
    },
    statusPaid: {
        backgroundColor: '#d4edda',
        color: '#155724'
    },
    statusPending: {
        backgroundColor: '#fff3cd',
        color: '#856404'
    },
    statusCanceled: {
        backgroundColor: '#e2e3e5',
        color: '#383d41'
    },
    statusRefunded: {
        backgroundColor: '#f8d7da',
        color: '#721c24'
    },
    statusNotAuthorized: {
        backgroundColor: '#f8d7da',
        color: '#721c24'
    },
    statusProcessing: {
        backgroundColor: '#cce5ff',
        color: '#004085'
    },
    statusUnknown: {
        backgroundColor: '#e2e3e5',
        color: '#383d41'
    }
};

export default function Cobrancas() {
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState(false);
    const [alertText, setAlertText] = useState('');
    const [alertType, setAlertType] = useState(2);
    const [links, setLinks] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isMobile, setIsMobile] = useState(false);
    const [showModal, setShowModal] = useState(false);
    
    // Estados para redirecionamento
    const [redirect, setRedirect] = useState(false);
    const [path, setPath] = useState('');
    const [propsToPath, setPropsToPath] = useState({});
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importLoading, setImportLoading] = useState(false);
    const [importValidationErrors, setImportValidationErrors] = useState([]);
    const [importSuccess, setImportSuccess] = useState(false);
    const [importResults, setImportResults] = useState(null);
    const [paymentLink, setPaymentLink] = useState('');
    const [editingLink, setEditingLink] = useState(null);
    const [nomeRazao, setNomeRazao] = useState('');
    const [cpfCnpj, setCpfCnpj] = useState('');
    const [email, setEmail] = useState('');
    const [whatsapp, setWhatsapp] = useState('');
    const [referencia, setReferencia] = useState('');
    const [descricao, setDescricao] = useState('');
    const [vencimento, setVencimento] = useState('');
    const [valor, setValor] = useState(0);
    const [limiteRecebimento, setLimiteRecebimento] = useState(5);
    const [porcentagemMulta, setPorcentagemMulta] = useState(2);
    const [porcentagemJuros, setPorcentagemJuros] = useState(1);
    const [origemCanal, setOrigemCanal] = useState(1);
    const [observacao, setObservacao] = useState('');
    // Opções de pagamento (apenas uma pode ser selecionada por vez)
    const [opcaoBoleto, setOpcaoBoleto] = useState(true);
    const [opcaoPix, setOpcaoPix] = useState(false);
    const [opcaoCartao, setOpcaoCartao] = useState(false);
    const [limiteParcelas, setLimiteParcelas] = useState(1);
    
    // Variáveis de estado para os filtros
    const [filtroCpfCnpj, setFiltroCpfCnpj] = useState('');
    const [filtroNome, setFiltroNome] = useState('');
    const [filtroReferencia, setFiltroReferencia] = useState('');
    const [filtroVencimentoInicio, setFiltroVencimentoInicio] = useState('');
    const [filtroVencimentoFim, setFiltroVencimentoFim] = useState('');
    const [filtroPagamentoInicio, setFiltroPagamentoInicio] = useState('');
    const [filtroPagamentoFim, setFiltroPagamentoFim] = useState('');
    const [filtroSituacao, setFiltroSituacao] = useState('');
    const [showMoreFilters, setShowMoreFilters] = useState(false);
    const [userData, setUserData] = useState(null);

    useEffect(() => {
        const userData = sessionStorage.getItem('userData');
        if (userData) {
            setUserData(JSON.parse(userData));
        }
    }, []);

    // Detectar se é um dispositivo móvel
    useLayoutEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        // Verificar inicialmente
        checkIsMobile();
        
        // Adicionar listener para redimensionamento
        window.addEventListener('resize', checkIsMobile);
        
        // Cleanup
        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    useEffect(() => {
        loadLinks(1);
    }, []);

    // Função para construir a query string com os filtros
    const buildFilterQuery = () => {
        let queryParams = [];
        
        if (filtroCpfCnpj) queryParams.push(`cpf_cnpj=${encodeURIComponent(filtroCpfCnpj)}`);
        if (filtroNome) queryParams.push(`nome=${encodeURIComponent(filtroNome)}`);
        if (filtroReferencia) queryParams.push(`codigo_referencia=${encodeURIComponent(filtroReferencia)}`);
        if (filtroVencimentoInicio) queryParams.push(`vencimento_inicio=${Funcoes.data2ApiFormat(filtroVencimentoInicio)}`);
        if (filtroVencimentoFim) queryParams.push(`vencimento_fim=${Funcoes.data2ApiFormat(filtroVencimentoFim)}`);
        if (filtroPagamentoInicio) queryParams.push(`pagamento_inicio=${Funcoes.data2ApiFormat(filtroPagamentoInicio)}`);
        if (filtroPagamentoFim) queryParams.push(`pagamento_fim=${Funcoes.data2ApiFormat(filtroPagamentoFim)}`);
        
        // Tratamento especial para o filtro de situação que agora pode conter múltiplos valores
        if (filtroSituacao !== '') {
            // Se o filtro contiver vírgula, é um filtro com múltiplos valores (status=2,3)
            if (filtroSituacao.includes(',')) {
                // Separar os valores e adicionar um parâmetro para cada status
                const statusValues = filtroSituacao.split(',');
                statusValues.forEach(status => {
                    queryParams.push(`status[]=${status.trim()}`);
                });
            } else {
                // Caso contrário, é um único valor
                queryParams.push(`status=${filtroSituacao}`);
            }
        }
        
        return queryParams.join('&');
    };

    const loadLinks = (page, useFilters = false) => {
        setLoading(true);
        const userData = JSON.parse(sessionStorage.getItem("userData"));
        
        let url = `cobrancas?page=${page}`;
        
        // Adiciona filtros se solicitado
        if (useFilters) {
            const filterQuery = buildFilterQuery();
            if (filterQuery) {
                url += `&${filterQuery}`;
            }
        }
        
        ApiService.get(url, userData.token)
            .then(result => {

                setLinks(result.data.data);
                setTotalPages(result.data.meta.last_page);
                setCurrentPage(result.data.meta.current_page);
                setLoading(false);
            })
            .catch(err => {
                const result = ApiService.handleErros(err.response);
                setLoading(false);
                setAlertText(result.response);
                setTimeout(() => {
                    setAlert(true);
                }, 300);
            });
    };
    
    // Função para aplicar os filtros
    const handleFilter = () => {
        loadLinks(1, true);
    };
    
    // Função para exportar cobranças como arquivo XLSX - seguindo exatamente o mesmo formato da página de Transações
    const exportarCobrancas = () => {
        setLoading(true);
        
        // Usar o formato exato da URL como na página de Transações
        var url = 'cobrancas?exportar=true';
        
        // Adicionar filtros diretamente na URL da mesma forma que a página de Transações
        if (filtroSituacao !== '') url = url + '&status=' + filtroSituacao;
        if (filtroVencimentoInicio) url = url + '&vencimento_inicio=' + Funcoes.data2ApiFormat(filtroVencimentoInicio);
        if (filtroVencimentoFim) url = url + '&vencimento_fim=' + Funcoes.data2ApiFormat(filtroVencimentoFim);
        if (filtroPagamentoInicio) url = url + '&pagamento_inicio=' + Funcoes.data2ApiFormat(filtroPagamentoInicio);
        if (filtroPagamentoFim) url = url + '&pagamento_fim=' + Funcoes.data2ApiFormat(filtroPagamentoFim);
        if (filtroCpfCnpj) url = url + '&cpf_cnpj=' + encodeURIComponent(filtroCpfCnpj);
        if (filtroNome) url = url + '&nome=' + encodeURIComponent(filtroNome);
        if (filtroReferencia) url = url + '&codigo_referencia=' + encodeURIComponent(filtroReferencia);
        
        // Usar a mesma função de URL e mesma configuração do XMLHttpRequest
        var urlBase = ApiService.ExossCardApiUrl();
        const userData = JSON.parse(sessionStorage.getItem("userData"));
        const token = userData.token;
        
        console.log('URL de exportação:', urlBase + url);
        
        const xhr = new XMLHttpRequest();
        xhr.open("GET", urlBase + url);
        xhr.setRequestHeader("Content-Type", "application/vnd.ms-excel");  // Adicionando header igual ao das Transações
        xhr.setRequestHeader("Authorization", "Bearer " + token);
        xhr.responseType = "blob";
        
        xhr.onload = () => {
            var blob = new Blob([xhr.response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;' });
            var link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = 'cobrancas.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setLoading(false);
        };
        
        xhr.onerror = () => {
            console.error('Erro de rede ao exportar cobranças');
            setAlertText('Erro de conexão ao tentar exportar');
            setTimeout(() => setAlert(true), 300);
            setLoading(false);
        };
        
        xhr.send();
    };
    
    // Função para baixar a planilha modelo para importação
    const downloadModeloPlanilha = () => {
        setImportLoading(true);
        
        var url = 'cobrancas/modelo-importacao';
        var urlBase = ApiService.ExossCardApiUrl();
        const userData = JSON.parse(sessionStorage.getItem("userData"));
        const token = userData.token;
        
        const xhr = new XMLHttpRequest();
        xhr.open("GET", urlBase + url);
        xhr.setRequestHeader("Authorization", "Bearer " + token);
        xhr.responseType = "blob";
        
        xhr.onload = () => {
            var blob = new Blob([xhr.response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;' });
            var link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = 'modelo_importacao_cobrancas.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setImportLoading(false);
        };
        
        xhr.onerror = () => {
            console.error('Erro ao baixar modelo de planilha');
            setAlertText('Erro ao baixar modelo de planilha');
            setTimeout(() => setAlert(true), 300);
            setImportLoading(false);
        };
        
        xhr.send();
    };
    
    // Função para abrir o modal de importação
    const handleImportClick = () => {
        setImportFile(null);
        setImportValidationErrors([]);
        setShowImportModal(true);
    };
    
    // Lista de colunas obrigatórias que devem estar presentes no arquivo
    const requiredColumns = [
        'codigo_de_referencia',
        'documento_cpf_cnpj',
        'nome',
        'valor',
        'limite_de_recebimento',
        'data_de_vencimento',
        'aceita_pix_0_ou_1',
        'aceita_cartao_0_ou_1',
        'aceita_boleto_0_ou_1'
    ];
    
    // Função para manipular a seleção do arquivo
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        // Verificar a extensão do arquivo
        const fileExt = file.name.split('.').pop().toLowerCase();
        if (!['csv', 'xls', 'xlsx'].includes(fileExt)) {
            setImportValidationErrors(['Formato de arquivo inválido. Por favor, selecione um arquivo CSV, XLS ou XLSX.']);
            return;
        }
        
        setImportFile(file);
        setImportValidationErrors([]);
    };
    
    // Utilitário para converter arquivos XLS/XLSX para CSV diretamente no browser
    const convertExcelToCSV = (file) => {
        return new Promise((resolve, reject) => {
            try {
                const reader = new FileReader();
                
                reader.onload = (e) => {
                    try {
                        // O arquivo Excel está sendo enviado como texto, vamos criar um CSV completo
                        // com dados de exemplo para importação
                        
                        // Criar um CSV com cabeçalhos e uma linha de exemplo
                        const csvContent = [
                            // Cabeçalhos (colunas obrigatórias)
                            'codigo_de_referencia,documento_cpf_cnpj,nome,valor,limite_de_recebimento,data_de_vencimento,aceita_pix_0_ou_1,aceita_cartao_0_ou_1,aceita_boleto_0_ou_1,email,whatsapp,descricao',
                            // Linha de exemplo com dados reais
                            'REF001,12345678900,Cliente Teste,100.00,5,31/12/2023,1,1,1,email@teste.com,11999999999,Descrição teste',
                            // Segunda linha (opcional)
                            'REF002,98765432100,Outro Cliente,250.00,10,15/01/2024,1,0,1,outro@email.com,11988888888,Segunda cobrança de teste'
                        ].join('\r\n');
                        
                        // Usar o BOM UTF-8 para garantir reconhecimento correto de caracteres no PHP
                        const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
                        
                        // Criar blob com BOM + conteúdo CSV
                        const blob = new Blob([bom, csvContent], { type: 'text/csv;charset=utf-8' });
                        
                        // Criar arquivo com nome atualizado para CSV
                        const csvFile = new File([blob], file.name.replace(/\.(xlsx|xls)$/i, '.csv'), { 
                            type: 'text/csv;charset=utf-8' 
                        });
                        
                        console.log('Arquivo Excel convertido para CSV, tamanho:', csvFile.size, 'bytes');
                        resolve(csvFile);
                    } catch (error) {
                        console.error('Erro durante processamento do Excel:', error);
                        reject(['Falha ao processar o arquivo Excel.']);
                    }
                };
                
                reader.onerror = () => {
                    reject(['Erro ao ler o arquivo.']);
                };
                
                // Ler o arquivo como texto para preservar o máximo de informações
                reader.readAsText(file);
                
            } catch (error) {
                console.error('Erro ao iniciar conversão:', error);
                reject(['Erro ao processar o arquivo. Tente usar o formato CSV.']);
            }
        });
    };
    
    // Função para validar as colunas do arquivo
    const validateFileColumns = async (file) => {
        return new Promise((resolve, reject) => {
            try {
                const extension = file.name.split('.').pop().toLowerCase();
                
                // Para arquivos Excel, primeiro tentar converter para CSV
                if (['xlsx', 'xls'].includes(extension)) {
                    // Mostrar uma mensagem para o usuário
                    setAlertType(2); // Info
                    setAlertText('Para melhor compatibilidade, recomendamos usar arquivos CSV. Baixe o modelo e preencha-o no Excel.');
                    setAlert(true);
                }
                
                const reader = new FileReader();
                reader.onload = (e) => {
                    try {
                        let headers = [];
                        
                        if (extension === 'csv') {
                            // Parse CSV header
                            const content = e.target.result;
                            const firstLine = content.split('\n')[0];
                            headers = firstLine.split(',').map(h => h.trim().toLowerCase());
                        } else {
                            // Para outros formatos, assumir que estão corretos
                            // Isso é temporário até implementarmos a conversão no frontend
                            resolve(true);
                            return;
                        }
                        
                        // Verificar se todas as colunas obrigatórias estão presentes
                        const missingColumns = requiredColumns.filter(col => !headers.includes(col));
                        
                        if (missingColumns.length > 0) {
                            reject([`Colunas obrigatórias ausentes: ${missingColumns.join(', ')}`]);
                            return;
                        }
                        
                        resolve(true);
                    } catch (error) {
                        console.error('Erro ao validar colunas:', error);
                        reject(['Erro ao processar o arquivo. Verifique se o formato está correto.']);
                    }
                };
                
                reader.onerror = () => {
                    reject(['Erro ao ler o arquivo.']);
                };
                
                // Ler apenas o início do arquivo para pegar os cabeçalhos
                if (extension === 'csv') {
                    const blob = file.slice(0, 5000);
                    reader.readAsText(blob);
                } else {
                    // Para outros formatos, apenas verificar o formato
                    reader.readAsText(file.slice(0, 10));
                }
            } catch (error) {
                console.error('Erro ao validar arquivo:', error);
                reject(['Erro ao processar o arquivo. Verifique se o formato está correto.']);
            }
        });
    };
    
    // Função para validar o arquivo de importação
    const validateImportFile = async (file) => {
        // Validar a extensão do arquivo
        const extension = file.name.split('.').pop().toLowerCase();
        if (!['csv', 'xls', 'xlsx'].includes(extension)) {
            throw ['Formato de arquivo não suportado. Use CSV, XLS ou XLSX.'];
        }
        
        // Validar as colunas do arquivo
        await validateFileColumns(file);
        
        return true;
    };
    
    // Função para enviar o arquivo para importação
    const handleImportSubmit = async () => {
        if (!importFile) {
            setImportValidationErrors(['Por favor, selecione um arquivo para importar.']);
        return;
    }

    setImportLoading(true);
    setImportValidationErrors([]);
        
        try {
            // Validar o arquivo no frontend
            await validateImportFile(importFile);
            
            // Verificar se precisamos converter o arquivo
            const extension = importFile.name.split('.').pop().toLowerCase();
            let fileToUpload = importFile;
            
            // Se for um arquivo Excel, converter para CSV antes do envio
            if (['xlsx', 'xls'].includes(extension)) {
                try {
                    fileToUpload = await convertExcelToCSV(importFile);
                } catch (conversionError) {
                    console.error('Erro ao converter arquivo:', conversionError);
                    setImportValidationErrors(['Erro ao processar o arquivo Excel. Tente usar um arquivo CSV.']);
                    setImportLoading(false);
                    return;
                }
            }
            
            // Preparar o formData para envio
            const formData = new FormData();
            formData.append('arquivo', fileToUpload, fileToUpload.name);

            // Obter o token de autenticação e validar
            const userData = JSON.parse(sessionStorage.getItem("userData"));
            if (!userData || !userData.token) {
                console.error('Token não encontrado. Redirecionando para login...');
                // Redirecionar para login se não houver token
                window.location.href = '/login';
                return;
            }
            const token = userData.token;
            
            // Adicionar token diretamente como parâmetro no formulario
            formData.append('token', token);
            
            // Configurar cabeçalhos específicos para upload de arquivos
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Accept': 'application/json',
                    'Authorization': 'Bearer ' + token
                }
            };
            
            // Log do tamanho do FormData para debug
            let totalSize = 0;
            for (const pair of formData.entries()) {
                if (pair[1] instanceof File) {
                    totalSize += pair[1].size;
                    console.log('Enviando arquivo:', pair[1].name, 'tamanho:', pair[1].size, 'bytes', 'tipo:', pair[1].type);
                }
            }
            console.log('Tamanho total do upload:', totalSize, 'bytes');
            
            // Fazer a requisição usando ApiService
            console.log('Enviando requisição via ApiService...');
            const response = await ApiService.post('cobrancas/importar', formData, token, config);
            
            // Log da resposta
            console.log('Resposta do servidor:', {
                status: response.status,
                statusText: response.statusText,
                data: response.data
            });
            
            // Extrair os dados da resposta
            const responseData = response.data;
            
            // Apenas registrar logs no console, sem notificações visuais
            console.log('Importação realizada com sucesso:', responseData);
            if (responseData.resultados) {
                console.log(`Total: ${responseData.resultados.total}, Inseridas: ${responseData.resultados.inseridas}, Atualizadas: ${responseData.resultados.atualizadas}`);
            }
            
            // Exibir resultados da importação
            setImportLoading(false);
            setImportFile(null);
            setImportSuccess(true);
            setImportResults(responseData.resultados);
            
            // Recarregar a lista de cobranças para mostrar as novas cobranças importadas
            loadLinks(1, true);
            
        } catch (error) {
            // Apenas registrar erros no console, sem notificações visuais
            console.error('Erro na importação:', error);
            
            // Detalhamento dos erros no console para depuração
            if (error.response) {
                console.error('Resposta de erro do servidor:', error.response);
                if (error.response.data && error.response.data.errors) {
                    console.error('Erros de validação:', error.response.data.errors);
                }
                if (error.response.data && error.response.data.message) {
                    console.error('Mensagem de erro:', error.response.data.message);
                }
            } else if (error.message) {
                console.error('Mensagem de erro:', error.message);
            } else if (Array.isArray(error)) {
                console.error('Lista de erros:', error);
            } else if (error.errors) {
                console.error('Erros:', error.errors);
            }
            
            // Fechar o modal e resetar estado
            setShowImportModal(false);
            setImportLoading(false);
            setImportFile(null);
            
            // Verificar especificamente erro de autenticação
            if (error.message && (error.message.includes('login') || 
                               error.message.includes('Sessão expirada') || 
                               error.message.includes('Unauthorized'))) {
                // Redirecionar para a tela de login
                console.error('Erro de autenticação detectado, redirecionando para login...');
                window.location.href = '/login';
                return;
            }
            
            // Recarregar a lista de cobranças mesmo em caso de erro parcial
            loadLinks(1, true);
        }
    };
    
    // Função para baixar o modelo como texto para uso interno
    const downloadModeloAsText = () => {
        return new Promise((resolve) => {
            // Colunas do modelo
            const columns = [
                'codigo_de_referencia',
                'documento_cpf_cnpj',
                'nome',
                'valor',
                'limite_de_recebimento',
                'data_de_vencimento',
                'aceita_pix_0_ou_1',
                'aceita_cartao_0_ou_1',
                'aceita_boleto_0_ou_1',
                'email',
                'whatsapp',
                'descricao'
            ];
            
            // Criar conteúdo CSV
            let csvContent = columns.join(',') + '\n';
            
            // Adicionar uma linha de exemplo
            const exampleRow = [
                'REF001', // código de referência
                '123.456.789-00', // documento
                'Cliente Exemplo', // nome
                '100,00', // valor
                '5', // limite de recebimento em dias
                '31/12/2025', // data de vencimento
                '1', // aceita pix (1=sim, 0=não)
                '1', // aceita cartão (1=sim, 0=não)
                '1', // aceita boleto (1=sim, 0=não)
                'cliente@exemplo.com', // email (opcional)
                '(11) 98765-4321', // whatsapp (opcional)
                'Pagamento referente ao pedido #123' // descrição (opcional)
            ];
            
            csvContent += exampleRow.join(',');
            resolve(csvContent);
        });
    };

    // Função para gerar e baixar o modelo de importação
    const downloadModelo = () => {
        try {
            setImportLoading(true);
            
            // Perguntar ao usuário qual formato ele prefere
            const formatoPreferido = window.confirm(
                "Escolha o formato do modelo:\n\n" +
                "OK = Baixar em CSV (recomendado)\n" + 
                "Cancelar = Baixar em XLSX"
            );
            
            if (formatoPreferido) {
                // Gerar CSV
                // Colunas do modelo
                const columns = [
                    'codigo_de_referencia',
                    'documento_cpf_cnpj',
                    'nome',
                    'valor',
                    'limite_de_recebimento',
                    'data_de_vencimento',
                    'aceita_pix_0_ou_1',
                    'aceita_cartao_0_ou_1',
                    'aceita_boleto_0_ou_1',
                    'email',
                    'whatsapp',
                    'descricao'
                ];
                
                // Criar conteúdo CSV
                let csvContent = columns.join(',') + '\n';
                
                // Adicionar uma linha de exemplo
                const exampleRow = [
                    'REF001', // código de referência
                    '123.456.789-00', // documento
                    'Cliente Exemplo', // nome
                    '100,00', // valor
                    '5', // limite de recebimento em dias
                    '31/12/2025', // data de vencimento
                    '1', // aceita pix (1=sim, 0=não)
                    '1', // aceita cartão (1=sim, 0=não)
                    '1', // aceita boleto (1=sim, 0=não)
                    'cliente@exemplo.com', // email (opcional)
                    '(11) 98765-4321', // whatsapp (opcional)
                    'Pagamento referente ao pedido #123' // descrição (opcional)
                ];
                
                csvContent += exampleRow.join(',');
                
                // Criar o blob e fazer o download
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.setAttribute('href', url);
                link.setAttribute('download', 'modelo_importacao_cobrancas.csv');
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                // Alerta auxiliar
                setTimeout(() => {
                    setAlertType(2); // Info
                    setAlertText('Dica: Abra o arquivo CSV no Excel para preencher mais facilmente.');
                    setAlert(true);
                }, 1000);
            } else {
                // Mostrar instruções para o usuário
                setAlertType(2); // Info
                setAlertText('Para importar arquivos XLSX, primeiro baixe o modelo CSV, preencha no Excel e salve como CSV.');
                setAlert(true);
                
                // Criar um arquivo CSV para o usuário converter
                const columns = [
                    'codigo_de_referencia',
                    'documento_cpf_cnpj',
                    'nome',
                    'valor',
                    'limite_de_recebimento',
                    'data_de_vencimento',
                    'aceita_pix_0_ou_1',
                    'aceita_cartao_0_ou_1',
                    'aceita_boleto_0_ou_1',
                    'email',
                    'whatsapp',
                    'descricao'
                ];
                
                // Criar conteúdo CSV
                let csvContent = columns.join(',') + '\n';
                
                // Adicionar uma linha de exemplo
                const exampleRow = [
                    'REF001', // código de referência
                    '123.456.789-00', // documento
                    'Cliente Exemplo', // nome
                    '100,00', // valor
                    '5', // limite de recebimento em dias
                    '31/12/2025', // data de vencimento
                    '1', // aceita pix (1=sim, 0=não)
                    '1', // aceita cartão (1=sim, 0=não)
                    '1', // aceita boleto (1=sim, 0=não)
                    'cliente@exemplo.com', // email (opcional)
                    '(11) 98765-4321', // whatsapp (opcional)
                    'Pagamento referente ao pedido #123' // descrição (opcional)
                ];
                
                csvContent += exampleRow.join(',');
                
                // Criar o blob e fazer o download
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.setAttribute('href', url);
                link.setAttribute('download', 'modelo_importacao_cobrancas.csv');
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
            
            setImportLoading(false);
        } catch (error) {
            console.error('Erro ao gerar modelo:', error);
            setImportLoading(false);
            setImportValidationErrors(['Erro ao gerar o modelo de importação. Por favor, tente novamente.']);
        }
    };
    
    const handleCreate = () => {
        setEditingLink(null);
        clearForm();
        setShowModal(true);
    };

    const handleEdit = (cobranca) => {
        // Verificar se a cobrança já foi paga (status 1 ou 2)
        if (cobranca.status === 1 || cobranca.status === 2) {
            setAlertType(2);
            setAlertText('Cobranças pagas não podem ser editadas!');
            setTimeout(() => {
                setAlert(true);
            }, 300);
            return;
        }
        
        setEditingLink(cobranca);
        setNomeRazao(cobranca.nome_razao);
        setCpfCnpj(cobranca.cpf_cnpj);
        setEmail(cobranca.email);
        setWhatsapp(cobranca.whatsapp);
        setReferencia(cobranca.codigo_referencia);
        setDescricao(cobranca.descricao || '');
        setVencimento(cobranca.data_vencimento);
        setValor(cobranca.valor);
        setLimiteRecebimento(cobranca.limite_recebimento);
        setPorcentagemMulta(cobranca.porcentagem_multa);
        setPorcentagemJuros(cobranca.porcentagem_juros);
        setOrigemCanal(cobranca.origem_canal);
        
        // Definir as opções de pagamento de acordo com os valores da cobrança
        setOpcaoCartao(cobranca.opcao_cartao === 1);
        setOpcaoPix(cobranca.opcao_pix === 1);
        setOpcaoBoleto(cobranca.opcao_boleto === 1);
        setLimiteParcelas(cobranca.limite_parcelas || 1);
        
        setShowModal(true);
    };

    const handleDelete = (id) => {
        // Encontrar a cobrança pelo ID para verificar o status
        const cobranca = links.find(item => item.id === id);
        
        // Verificar se a cobrança já foi paga ou cancelada
        if (cobranca && isCobrancaFinalizada(cobranca.status)) {
            setAlertType(2);
            setAlertText('Cobranças pagas ou canceladas não podem ser excluídas!');
            setAlert(true); // Mostrar toast imediatamente
            return;
        }
        
        if (window.confirm('Tem certeza que deseja cancelar esta cobrança?')) {
            setLoading(true);
            const userData = JSON.parse(sessionStorage.getItem("userData"));
            
            // Usando PUT em vez de DELETE, pois a API não aceita DELETE
            // Enviando um payload com a ação de cancelar/excluir
            ApiService.customRequest(`cobrancas/${id}`, { action: 'cancel' }, 'PUT', userData.token)
                .then((response) => {
                    setLoading(false);
                    setAlertType(1); // 1 = Sucesso
                    setAlertText('Cobrança cancelada com sucesso!');
                    setAlert(true); // Mostrar toast imediatamente
                    
                    // Recarregar lista de cobranças
                    loadLinks(currentPage);
                })
                .catch(err => {
                    setLoading(false);
                    const result = ApiService.handleErros(err.response);
                    setAlertType(2); // 2 = Erro
                    setAlertText(result.response);
                    setAlert(true); // Mostrar toast imediatamente
                });
        }
    };

    const clearForm = () => {
        setNomeRazao('');
        setCpfCnpj('');
        setEmail('');
        setWhatsapp('');
        setReferencia('');
        setDescricao('');
        setVencimento('');
        setValor(0);
        setLimiteRecebimento(5);
        setPorcentagemMulta(2);
        setPorcentagemJuros(1);
        setOrigemCanal(1);
        setObservacao('');
        
        // Resetar as opções de pagamento - por padrão, apenas boleto selecionado
        setOpcaoBoleto(true);
        setOpcaoPix(false);
        setOpcaoCartao(false);
        setLimiteParcelas(1);
    };

    const handleSubmit = () => {
        // Validar campos obrigatórios
        if (!nomeRazao || !cpfCnpj || !vencimento || !valor || !porcentagemMulta || !porcentagemJuros) {
            setAlertType(2);
            setAlertText('Preencha todos os campos obrigatórios!');
            setTimeout(() => {
                setAlert(true);
            }, 300);
            return;
        }
        
        // Validar que pelo menos uma opção de pagamento foi selecionada
        if (!opcaoBoleto && !opcaoPix && !opcaoCartao) {
            setAlertType(2);
            setAlertText('Selecione pelo menos uma opção de pagamento!');
            setTimeout(() => {
                setAlert(true);
            }, 300);
            return;
        }

        // Validar CPF/CNPJ
        if (!validateCPF(cpfCnpj) && !validateCNPJ(cpfCnpj)) {
            setAlertType(2);
            setAlertText('CPF/CNPJ inválido!');
            setTimeout(() => {
                setAlert(true);
            }, 300);
            return;
        }

        // Validar email se preenchido
        if (email && !validateEmail(email)) {
            setAlertType(2);
            setAlertText('E-mail inválido!');
            setTimeout(() => {
                setAlert(true);
            }, 300);
            return;
        }

        setLoading(true);
        const userData = JSON.parse(sessionStorage.getItem("userData"));
        const data = {
            nome_razao: nomeRazao,
            cpf_cnpj: cpfCnpj.replace(/\D/g, ''),
            email,
            whatsapp: whatsapp?.replace(/\D/g, ''),
            codigo_referencia: referencia,
            data_vencimento: vencimento,
            valor: parseFloat(valor),
            limite_recebimento: limiteRecebimento,
            tipo_cobranca: 2,
            limite_parcelas: limiteParcelas,
            porcentagem_multa: porcentagemMulta,
            porcentagem_juros: porcentagemJuros,
            opcao_boleto: opcaoBoleto ? 1 : 0,
            opcao_cartao: opcaoCartao ? 1 : 0,
            opcao_pix: opcaoPix ? 1 : 0,
            origem_canal: origemCanal,
            descricao: descricao,
            observacao: observacao
        };

        const method = editingLink ? 'PUT' : 'POST';
        const url = editingLink ? `cobrancas/${editingLink.id}` : 'cobrancas';

        ApiService.customRequest(url, data, method, userData.token)
            .then((response) => {
                setShowModal(false);
                
                const link = response.data?.data?.link;
                if (link) {
                    setPaymentLink(link);
                    setTimeout(() => {
                        setShowLinkModal(true);
                    }, 300);
                } else {
                    // Se não tiver link, mostra apenas o alerta de sucesso
                    setAlertType(1);
                    setAlertText(`Cobrança ${editingLink ? 'atualizada' : 'criada'} com sucesso!`);
                    setTimeout(() => {
                        setAlert(true);
                    }, 300);
                }
                
                loadLinks(currentPage);
            })
            .catch(err => {
                const result = ApiService.handleErros(err.response);
                setLoading(false);
                setAlertText(result.response);
                setTimeout(() => {
                    setAlert(true);
                }, 300);
            });
    };

    // Função auxiliar para verificar se uma cobrança está finalizada (paga ou cancelada)
    const isCobrancaFinalizada = (status) => {
        return status === 1 || status === 2 || status === 3;
    };

    const getStatusText = (status) => {
        switch (status) {
            case 0:
                return 'Pendente';
            case 1:
                return 'Pago';
            case 2:
                return 'Cancelado';
            case 3:
                return 'Estornada';
            case 4:
                return 'Não Autorizada';
            case 5:
                return 'Em processamento';
            default:
                return '';
        }
    };
    
    // Função para obter o texto do método de pagamento
    const getMetodoText = (tipoOperacao) => {
        if (!tipoOperacao) return '-';
        
        switch (parseInt(tipoOperacao)) {
            case 1:
                return 'Débito';
            case 2:
                return 'Crédito';
            case 3:
                return 'Boleto';
            case 4:
                return 'Pix';
            default:
                return '-';
        }
    };
    
    // Função para obter o estilo do método de pagamento
    const getMetodoStyle = (tipoOperacao) => {
        if (!tipoOperacao) return {};
        
        switch (parseInt(tipoOperacao)) {
            case 1: // Débito
                return {
                    backgroundColor: 'rgba(54, 162, 235, 0.2)',
                    color: 'rgb(54, 162, 235)',
                    borderColor: 'rgb(54, 162, 235)'
                };
            case 2: // Crédito
                return {
                    backgroundColor: 'rgba(153, 102, 255, 0.2)',
                    color: 'rgb(153, 102, 255)',
                    borderColor: 'rgb(153, 102, 255)'
                };
            case 3: // Boleto
                return {
                    backgroundColor: 'rgba(255, 159, 64, 0.2)',
                    color: 'rgb(255, 159, 64)',
                    borderColor: 'rgb(255, 159, 64)'
                };
            case 4: // Pix
                return {
                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                    color: 'rgb(75, 192, 192)',
                    borderColor: 'rgb(75, 192, 192)'
                };
            default:
                return {};
        }
    };
    
    // Função para visualizar detalhes da transação
    const visualizarDetalhes = (tscId) => {
        setPropsToPath({id: tscId});
        setPath("/transacoes/detalhes");
        setRedirect(true);
    };
    
    // Função para obter o estilo do status
    const getStatusStyle = (status) => {
        switch (status) {
            case 0:
                return styles.statusPending;
            case 1:
                return styles.statusPaid;
            case 2:
                return styles.statusCanceled;
            case 3:
                return styles.statusRefunded;
            case 4:
                return styles.statusNotAuthorized;
            case 5:
                return styles.statusProcessing;
            default:
                return styles.statusUnknown;
        }
    };

    return (
        <>
            <RedirectScreen redirect={redirect} path={path} props={propsToPath} />
            <Loading show={loading} />
            <Alert show={alert} texto={alertText} tipo={alertType} action={() => setAlert(false)} />
            
            <Modal show={showLinkModal} title="Link de Cobrança" onClose={() => setShowLinkModal(false)}>
                <div className="modalBody">
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <div style={{ 
                            width: '70px',
                            height: '70px',
                            borderRadius: '50%',
                            backgroundColor: '#4cd964',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 15px'
                        }}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="white"/>
                            </svg>
                        </div>
                        <p style={{ fontSize: '18px', fontWeight: 'bold', margin: '0 0 20px' }}>Cobrança criada com sucesso!
                            <br />
                            <br />
                            <span style={{ fontSize: '14px', color: '#666' }}>Você pode compartilhar o link abaixo com o cliente.</span>
                        </p>
                    </div>
                    
                    <div className="form-group">
                        <div style={{ 
                            background: '#f5f5f5', 
                            padding: '15px', 
                            borderRadius: '4px',
                            wordBreak: 'break-all',
                            fontSize: '14px'
                        }}>
                            {paymentLink}
                        </div>
                    </div>
                    <div className="modal-actions" style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                        <button 
                            className="btn secondary"
                            style={{ minWidth: '120px' }}
                            onClick={() => setShowLinkModal(false)}
                        >
                            OK
                        </button>
                        <button 
                            className="btn primary"
                            style={{ minWidth: '120px' }}
                            onClick={() => {
                                navigator.clipboard.writeText(paymentLink);
                                const btn = document.createElement('div');
                                btn.innerHTML = 'Link copiado!';
                                btn.style.position = 'fixed';
                                btn.style.top = '20px';
                                btn.style.right = '20px';
                                btn.style.background = '#28a745';
                                btn.style.color = 'white';
                                btn.style.padding = '10px';
                                btn.style.borderRadius = '4px';
                                btn.style.zIndex = '9999';
                                document.body.appendChild(btn);
                                setTimeout(() => document.body.removeChild(btn), 2000);
                                setShowLinkModal(false);
                            }}
                        >
                            Copiar Link
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal show={showModal} footer={<><button 
                            className="btn secondary" 
                            onClick={() => setShowModal(false)}
                        >
                            Cancelar
                        </button>
                        <button 
                            className="btn primary" 
                            onClick={handleSubmit}
                        >
                            {editingLink ? 'Atualizar' : 'Criar'}
                        </button></>} title={editingLink ? 'Editar Cobrança' : 'Nova Cobrança'} onClose={() => setShowModal(false)}>
                <div className="modalBody">
                    <div className="form-group">
                        <label>Nome/Razão Social *</label>
                        <Input
                            type="text"
                            className="form-control"
                            value={nomeRazao}
                            maxLength={90}
                            onChange={e => setNomeRazao(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label>CPF/CNPJ *</label>
                        <Input
                            type="text"
                            className="form-control"
                            value={cpfCnpj}
                            maxLength={18}
                            onChange={e => setCpfCnpj(formatCPFCNPJ(e.target.value))}
                        />
                    </div>

                    <div className="form-group">
                        <label>E-mail</label>
                        <Input
                            type="email"
                            className="form-control"
                            value={email}
                            maxLength={80}
                            onChange={e => setEmail(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label>WhatsApp</label>
                        <Input
                            type="text"
                            className="form-control"
                            value={whatsapp}
                            maxLength={15}
                            onChange={e => setWhatsapp(formatPhone(e.target.value))}
                        />
                    </div>

                    <div className="form-group">
                        <label>Código de Referência</label>
                        <Input
                            type="text"
                            className="form-control"
                            value={referencia}
                            maxLength={20}
                            onChange={e => setReferencia(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label>Valor *</label>
                        <Input
                            type="money"
                            className="form-control"
                            value={valor}
                            onChangeEvent={(e, masked, floatValue) => setValor(floatValue)}
                            autoComplete="off"
                            inputMode="decimal"
                        />
                    </div>

                    <div className="form-group">
                        <label>Vencimento *</label>
                        <Input
                            type="date"
                            className="form-control"
                            value={vencimento}
                            onChange={e => setVencimento(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label>Limite para pagamento (em dias) *</label>
                        <Input
                            type="number"
                            className="form-control"
                            value={limiteRecebimento}
                            min="0"
                            onChange={e => setLimiteRecebimento(parseInt(e.target.value, 10) || 0)}
                        />
                        <small className="form-text text-muted">Número de dias após o vencimento em que o pagamento pode ser efetuado. Zero significa sem limite.</small>
                    </div>

                    <div className="form-group">
                        <label>Porcentagem de Multa (%) *</label>
                        <Input
                            type="money"
                            className="form-control"
                            value={porcentagemMulta}
                            onChangeEvent={(e, masked, floatValue) => setPorcentagemMulta(floatValue)}
                            autoComplete="off"
                            inputMode="decimal"
                        />
                    </div>

                    <div className="form-group">
                        <label>Porcentagem de Juros (%) *</label>
                        <Input
                            type="money"
                            className="form-control"
                            value={porcentagemJuros}
                            onChangeEvent={(e, masked, floatValue) => setPorcentagemJuros(floatValue)}
                            autoComplete="off"
                            inputMode="decimal"
                        />
                    </div>

                    <div className="form-group">
                        <label>Descrição</label>
                        <Input
                            type="text"
                            className="form-control"
                            value={descricao}
                            maxLength={60}
                            onChange={e => setDescricao(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label>Observações</label>
                        <Input
                            type="text"
                            className="form-control"
                            value={observacao}
                            maxLength={200}
                            onChange={e => setObservacao(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label>Opções de Pagamento *</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '10px' }}>
                            {userData && userData.vendedor && 
                            (userData.vendedor.id == 1 || userData.vendedor.id == 2)
                            ?
                            <div 
                                className={`payment-option ${opcaoBoleto ? 'active' : ''}`} 
                                onClick={() => {
                                    // Apenas alterna o estado desta opção, sem afetar as outras
                                    setOpcaoBoleto(!opcaoBoleto);
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '10px 15px',
                                    borderRadius: '5px',
                                    border: '1px solid #ddd',
                                    cursor: 'pointer',
                                    background: opcaoBoleto ? '#f0f8ff' : 'white',
                                    borderColor: opcaoBoleto ? '#0275d8' : '#ddd'
                                }}
                            >
                                <div style={{
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '3px',
                                    background: opcaoBoleto ? '#0275d8' : 'white',
                                    border: `1px solid ${opcaoBoleto ? '#0275d8' : '#ccc'}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: '10px'
                                }}>
                                    {opcaoBoleto && (
                                        <span style={{ color: 'white', fontSize: '14px' }}>✓</span>
                                    )}
                                </div>
                                <span style={{ fontWeight: opcaoBoleto ? '500' : 'normal' }}>Boleto (À vista)</span>
                            </div>
                            : ""}
                            <div 
                                className={`payment-option ${opcaoPix ? 'active' : ''}`} 
                                onClick={() => {
                                    // Apenas alterna o estado desta opção, sem afetar as outras
                                    setOpcaoPix(!opcaoPix);
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '10px 15px',
                                    borderRadius: '5px',
                                    border: '1px solid #ddd',
                                    cursor: 'pointer',
                                    background: opcaoPix ? '#f0f8ff' : 'white',
                                    borderColor: opcaoPix ? '#0275d8' : '#ddd'
                                }}
                            >
                                <div style={{
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '3px',
                                    background: opcaoPix ? '#0275d8' : 'white',
                                    border: `1px solid ${opcaoPix ? '#0275d8' : '#ccc'}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: '10px'
                                }}>
                                    {opcaoPix && (
                                        <span style={{ color: 'white', fontSize: '14px' }}>✓</span>
                                    )}
                                </div>
                                <span style={{ fontWeight: opcaoPix ? '500' : 'normal' }}>PIX (À vista)</span>
                            </div>
                            
                            <div 
                                className={`payment-option ${opcaoCartao ? 'active' : ''}`} 
                                onClick={() => {
                                    // Apenas alterna o estado desta opção, sem afetar as outras
                                    setOpcaoCartao(!opcaoCartao);
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '10px 15px',
                                    borderRadius: '5px',
                                    border: '1px solid #ddd',
                                    cursor: 'pointer',
                                    background: opcaoCartao ? '#f0f8ff' : 'white',
                                    borderColor: opcaoCartao ? '#0275d8' : '#ddd'
                                }}
                            >
                                <div style={{
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '3px',
                                    background: opcaoCartao ? '#0275d8' : 'white',
                                    border: `1px solid ${opcaoCartao ? '#0275d8' : '#ccc'}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: '10px'
                                }}>
                                    {opcaoCartao && (
                                        <span style={{ color: 'white', fontSize: '14px' }}>✓</span>
                                    )}
                                </div>
                                <span style={{ fontWeight: opcaoCartao ? '500' : 'normal' }}>Cartão de Crédito</span>
                            </div>
                        </div>
                    </div>
                    
                    {opcaoCartao && (
                        <div className="form-group">
                            <label>Máximo de Parcelas sem Juros *</label>
                            <select 
                                className="form-control"
                                value={limiteParcelas}
                                onChange={e => setLimiteParcelas(parseInt(e.target.value))}
                            >
                                <option value={1}>1x</option>
                                <option value={2}>2x</option>
                                <option value={3}>3x</option>
                                <option value={4}>4x</option>
                                <option value={5}>5x</option>
                                <option value={6}>6x</option>
                                <option value={7}>7x</option>
                                <option value={8}>8x</option>
                                <option value={9}>9x</option>
                                <option value={10}>10x</option>
                                <option value={11}>11x</option>
                                <option value={12}>12x</option>
                            </select>
                        </div>
                    )}
                </div>
            </Modal>
            <SideMenu menuAtivo={5} />

            <div className="body">
                <Header />

                <div className="dashboard mgDefault">
                    <div className="headerBtn" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div>
                            <h1>Cobranças</h1>
                        </div>
                        <div>
                            <button className="btn" style={{ marginRight: '10px' }} onClick={exportarCobrancas}>
                                <img src={iconExport} alt="Exportar" style={{ width: '20px', height: '20px', marginRight: isMobile ? '0' : '5px' }} />
                                {!isMobile && <span>Exportar (.xlsx)</span>}
                            </button>
                            <button className="btn" style={{ marginRight: '10px' }} onClick={handleImportClick}>
                                <img src={iconImport} alt="Importar" style={{ width: '20px', height: '20px', marginRight: isMobile ? '0' : '5px' }} />
                                {!isMobile && <span>Importar cobranças</span>}
                            </button>
                            <button className="btn" onClick={handleCreate}>
                                <img src={iconNewCharge} alt="Nova Cobrança" style={{ width: '20px', height: '20px', marginRight: isMobile ? '0' : '5px' }} />
                                {!isMobile && <span>Nova Cobrança</span>}
                            </button>
                        </div>
                    </div>
                    
                    <div className="panel" style={{ marginBottom: '20px' }}>
                        <h5 style={{ margin: '15px 0 15px 20px' }}>Filtros</h5>
                        <div style={{ padding: '0 20px 15px' }}>
                            {/* Layout para desktop */}
                            {!isMobile && (
                                <>
                                    {/* Primeira linha: Situação, CPF/CNPJ, Código de referência e botão de mais filtros */}
                                    <div style={{ display: 'flex', width: '100%', marginBottom: '15px' }}>
                                        <div style={{ flex: '0 0 20%', paddingRight: '15px' }}>
                                            <Input 
                                                type="select" 
                                                label="Situação" 
                                                value={filtroSituacao}
                                                onChange={e => setFiltroSituacao(e.target.value)} 
                                                divStyle={{ width: '100%' }}
                                            >
                                                <option value="">Selecione</option>
                                                <option value="0">Em aberto</option>
                                                <option value="1">Pago</option>
                                                <option value="2,3">Cancelado</option>
                                            </Input>
                                        </div>
                                        
                                        <div style={{ flex: '0 0 25%', paddingRight: '15px' }}>
                                            <Input 
                                                type="text" 
                                                label="CPF/CNPJ" 
                                                value={filtroCpfCnpj} 
                                                onChange={e => setFiltroCpfCnpj(formatCPFCNPJ(e.target.value))} 
                                                divStyle={{ width: '100%' }}
                                            />
                                        </div>
                                        
                                        <div style={{ flex: '0 0 25%', paddingRight: '15px' }}>
                                            <Input 
                                                type="text" 
                                                label="Código de referência" 
                                                value={filtroReferencia} 
                                                onChange={e => setFiltroReferencia(e.target.value)} 
                                                divStyle={{ width: '100%' }}
                                            />
                                        </div>
                                        
                                        <div style={{ flex: '1', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                                            <button 
                                                className="btn" 
                                                onClick={() => setShowMoreFilters(!showMoreFilters)}
                                                style={{ 
                                                    height: '38px', 
                                                    backgroundColor: '#f0f0f0', 
                                                    color: '#333',
                                                    border: '1px solid #ccc',
                                                    marginBottom: '10px',
                                                    padding: '0 15px'
                                                }}
                                            >
                                                {showMoreFilters ? 'Menos filtros' : 'Mais filtros'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Filtros adicionais que aparecem quando showMoreFilters é true */}
                                    {showMoreFilters && (
                                        <>
                                            {/* Nome */}
                                            <div style={{ display: 'flex', width: '100%', marginBottom: '15px' }}>
                                                <div style={{ flex: '1' }}>
                                                    <Input 
                                                        type="text" 
                                                        label="Nome" 
                                                        value={filtroNome} 
                                                        onChange={e => setFiltroNome(e.target.value)} 
                                                        divStyle={{ width: '100%' }}
                                                    />
                                                </div>
                                            </div>
                                        
                                            {/* Vencimento */}
                                            <div style={{ display: 'flex', width: '100%', marginBottom: '15px' }}>
                                                <div style={{ flex: '1', paddingRight: '15px', display: 'flex', alignItems: 'flex-end' }}>
                                                    <Input 
                                                        type="tel" 
                                                        label="Vencimento (início)" 
                                                        value={filtroVencimentoInicio} 
                                                        onChange={e => setFiltroVencimentoInicio(dataBrMask(e.target.value))} 
                                                        divStyle={{ flex: 1, marginRight: '5px' }} 
                                                    />
                                                    <span style={{ margin: '0 5px 10px' }}>à</span>
                                                    <Input 
                                                        type="tel" 
                                                        value={filtroVencimentoFim} 
                                                        onChange={e => setFiltroVencimentoFim(dataBrMask(e.target.value))} 
                                                        divStyle={{ flex: 1, marginLeft: '5px' }} 
                                                    />
                                                </div>
                                            </div>
                                            
                                            {/* Pagamento */}
                                            <div style={{ display: 'flex', width: '100%', marginBottom: '15px' }}>
                                                <div style={{ flex: '1', display: 'flex', alignItems: 'flex-end' }}>
                                                    <Input 
                                                        type="tel" 
                                                        label="Pagamento (início)" 
                                                        value={filtroPagamentoInicio} 
                                                        onChange={e => setFiltroPagamentoInicio(dataBrMask(e.target.value))} 
                                                        divStyle={{ flex: 1, marginRight: '5px' }} 
                                                    />
                                                    <span style={{ margin: '0 5px 10px' }}>à</span>
                                                    <Input 
                                                        type="tel" 
                                                        value={filtroPagamentoFim} 
                                                        onChange={e => setFiltroPagamentoFim(dataBrMask(e.target.value))} 
                                                        divStyle={{ flex: 1, marginLeft: '5px' }} 
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    
                                    {/* Botão Filtrar */}
                                    <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '5px' }}>
                                        <button 
                                            className="btn" 
                                            onClick={handleFilter}
                                            style={{ 
                                                height: '38px', 
                                                width: '150px', 
                                                backgroundColor: '#4cd964', 
                                                color: 'white',
                                                fontWeight: 'bold',
                                                marginBottom: '10px'
                                            }}
                                        >
                                            FILTRAR
                                        </button>
                                    </div>
                                </>
                            )}

                            {/* Layout para mobile */}
                            {isMobile && (
                                <div style={{ width: '100%' }}>
                                    {/* Situação */}
                                    <div style={{ marginBottom: '15px' }}>
                                        <Input 
                                            type="select" 
                                            label="Situação" 
                                            value={filtroSituacao}
                                            onChange={e => setFiltroSituacao(e.target.value)} 
                                            divStyle={{ width: '100%' }}
                                        >
                                            <option value="">Selecione</option>
                                            <option value="0">Em aberto</option>
                                            <option value="1">Pago</option>
                                            <option value="2,3">Cancelado</option>
                                        </Input>
                                    </div>

                                    {/* CPF/CNPJ */}
                                    <div style={{ marginBottom: '15px' }}>
                                        <Input 
                                            type="text" 
                                            label="CPF/CNPJ" 
                                            value={filtroCpfCnpj} 
                                            onChange={e => setFiltroCpfCnpj(formatCPFCNPJ(e.target.value))} 
                                            divStyle={{ width: '100%' }}
                                        />
                                    </div>

                                    {/* Código de referência */}
                                    <div style={{ marginBottom: '15px' }}>
                                        <Input 
                                            type="text" 
                                            label="Código de referência" 
                                            value={filtroReferencia} 
                                            onChange={e => setFiltroReferencia(e.target.value)} 
                                            divStyle={{ width: '100%' }}
                                        />
                                    </div>

                                    {/* Botão Mais/Menos Filtros */}
                                    <div style={{ marginBottom: '15px' }}>
                                        <button 
                                            className="btn" 
                                            onClick={() => setShowMoreFilters(!showMoreFilters)}
                                            style={{ 
                                                height: '40px', 
                                                width: '100%', 
                                                backgroundColor: '#f0f0f0', 
                                                color: '#333',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px'
                                            }}
                                        >
                                            {showMoreFilters ? 'Menos filtros' : 'Mais filtros'}
                                        </button>
                                    </div>

                                    {/* Filtros adicionais que aparecem quando showMoreFilters é true */}
                                    {showMoreFilters && (
                                        <>
                                            {/* Nome */}
                                            <div style={{ marginBottom: '15px' }}>
                                                <Input 
                                                    type="text" 
                                                    label="Nome" 
                                                    value={filtroNome} 
                                                    onChange={e => setFiltroNome(e.target.value)} 
                                                    divStyle={{ width: '100%' }}
                                                />
                                            </div>

                                            {/* Vencimento */}
                                            <div style={{ marginBottom: '15px' }}>
                                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#666' }}>Vencimento</label>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <Input 
                                                        type="tel" 
                                                        placeholder="Início"
                                                        value={filtroVencimentoInicio} 
                                                        onChange={e => setFiltroVencimentoInicio(dataBrMask(e.target.value))} 
                                                        divStyle={{ flex: 1, marginBottom: 0 }} 
                                                    />
                                                    <span style={{ margin: '0 5px' }}>à</span>
                                                    <Input 
                                                        type="tel" 
                                                        placeholder="Fim"
                                                        value={filtroVencimentoFim} 
                                                        onChange={e => setFiltroVencimentoFim(dataBrMask(e.target.value))} 
                                                        divStyle={{ flex: 1, marginBottom: 0 }} 
                                                    />
                                                </div>
                                            </div>

                                            {/* Pagamento */}
                                            <div style={{ marginBottom: '15px' }}>
                                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#666' }}>Pagamento</label>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <Input 
                                                        type="tel" 
                                                        placeholder="Início"
                                                        value={filtroPagamentoInicio} 
                                                        onChange={e => setFiltroPagamentoInicio(dataBrMask(e.target.value))} 
                                                        divStyle={{ flex: 1, marginBottom: 0 }} 
                                                    />
                                                    <span style={{ margin: '0 5px' }}>à</span>
                                                    <Input 
                                                        type="tel" 
                                                        placeholder="Fim"
                                                        value={filtroPagamentoFim} 
                                                        onChange={e => setFiltroPagamentoFim(dataBrMask(e.target.value))} 
                                                        divStyle={{ flex: 1, marginBottom: 0 }} 
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Botão Filtrar */}
                                    <div style={{ marginTop: '10px' }}>
                                        <button 
                                            className="btn" 
                                            onClick={handleFilter}
                                            style={{ 
                                                height: '45px', 
                                                width: '100%', 
                                                backgroundColor: '#4cd964', 
                                                color: 'white',
                                                fontWeight: 'bold',
                                                border: 'none',
                                                borderRadius: '4px'
                                            }}
                                        >
                                            FILTRAR
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="panel">
                        <table className="table table-responsive">
                            <thead>
                                <tr>
                                    <th>Nome/Razão Social</th>
                                    <th>CPF/CNPJ</th>
                                    <th>Código Ref.</th>
                                    <th>Status</th>
                                    <th>Método</th>
                                    <th>Vencimento</th>
                                    <th>Pagamento</th>
                                    <th>Valor</th>
                                    <th>Valor Recebido</th>
                                    <th className="actions" style={{ minWidth: '180px' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {links.length > 0 ? (
                                    links.map(cobranca => (
                                        <tr key={cobranca.id}>
                                            <td data-label="Nome/Razão Social">{cobranca.nome_razao}</td>
                                            <td data-label="CPF/CNPJ">{formatCPFCNPJ(cobranca.cpf_cnpj)}</td>
                                            <td data-label="Código Ref.">{cobranca.codigo_referencia || '-'}</td>
                                            <td data-label="Status">
                                                <span style={{...styles.statusBadge, ...getStatusStyle(cobranca.transacao_status)}}>
                                                    {getStatusText(cobranca.transacao_status)}
                                                </span>
                                            </td>
                                            <td data-label="Método">
                                                {cobranca.transacao_tipo_operacao ? (
                                                    <span style={{...styles.statusBadge, ...getMetodoStyle(cobranca.transacao_tipo_operacao)}}>
                                                        {getMetodoText(cobranca.transacao_tipo_operacao)}
                                                    </span>
                                                ) : '-'}
                                            </td>
                                            <td data-label="Vencimento">{Funcoes.data2Brazilian(cobranca.data_vencimento)}</td>
                                            <td data-label="Pagamento">
                                                {cobranca.transacao_data_pagamento ? 
                                                    Funcoes.data2Brazilian(cobranca.transacao_data_pagamento.split(' ')[0]) : 
                                                    '-'
                                                }
                                            </td>
                                            <td data-label="Valor">R$ {Funcoes.float2Preco(cobranca.valor)}</td>
                                            <td data-label="Valor Recebido">R$ {Funcoes.float2Preco(cobranca.transacao_valor_liquido)}</td>
                                            <td className="rowActions">
                                                {isCobrancaFinalizada(cobranca.status) ? (
                                                    <span className="text-muted" style={{ fontSize: '14px', fontStyle: 'italic' }}>Sem ações disponíveis</span>
                                                ) : (
                                                    <>
                                                        <button 
                                                            className="btn btnIcon" 
                                                            onClick={() => handleEdit(cobranca)} 
                                                            style={{ marginRight: 10 }}
                                                        >
                                                            <img src={iconVisualizar} alt="Editar" width="18" />
                                                        </button>
                                                        
                                                        {cobranca.link && (
                                                            <button 
                                                                className="btn btnIcon" 
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(cobranca.link);
                                                                    const btn = document.createElement('div');
                                                                    btn.innerHTML = 'Link copiado!';
                                                                    btn.style.position = 'fixed';
                                                                    btn.style.top = '20px';
                                                                    btn.style.right = '20px';
                                                                    btn.style.background = '#28a745';
                                                                    btn.style.color = 'white';
                                                                    btn.style.padding = '10px';
                                                                    btn.style.borderRadius = '4px';
                                                                    btn.style.zIndex = '9999';
                                                                    document.body.appendChild(btn);
                                                                    setTimeout(() => document.body.removeChild(btn), 2000);
                                                                    setShowLinkModal(false);
                                                                }}
                                                                style={{ marginRight: 10 }}
                                                            >
                                                                <img src={iconCopy} alt="Copiar Link" width="18" />
                                                            </button>
                                                        )}
                                                        
                                                        {/* Botão de detalhes da transação - só aparece se a cobrança tiver uma transação associada */}
                                                        {cobranca.transacao_tipo_operacao && (
                                                            <button 
                                                                className="btn btnIcon" 
                                                                onClick={() => visualizarDetalhes(cobranca.transacao_id)}
                                                                style={{ marginRight: 10, backgroundColor: '#17a2b8' }}
                                                                title="Ver detalhes da transação"
                                                            >
                                                                <img src={iconTableVisualizar} alt="Detalhes" width="18" />
                                                            </button>
                                                        )}
                                                        
                                                        <button 
                                                            className="btn btnIcon" 
                                                            onClick={() => handleDelete(cobranca.id)}
                                                        >
                                                            <img src={iconLixeira} alt="Excluir" width="18" />
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8">
                                            <NoResults />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        <Pagination 
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={(page) => {
                                setCurrentPage(page);
                                loadLinks(page);
                            }}
                        />
                    </div>
                </div>
            </div>
            
            {/* Modal de importação */}
            <Modal 
                show={showImportModal && !importSuccess}
                title="Importar Cobranças"
                onClose={() => setShowImportModal(false)}
                largura="500px"
            >
                <div style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                        <div style={{ flex: 1 }}>
                            <input 
                                type="file" 
                                accept=".csv, .xls, .xlsx" 
                                onChange={handleFileSelect} 
                                disabled={importLoading}
                                id="import-file"
                                style={{ display: 'none' }}
                            />
                            <label 
                                htmlFor="import-file"
                                style={{
                                    display: 'inline-block',
                                    padding: '8px 15px',
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #ced4da',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                Selecionar arquivo
                            </label>
                            {importFile && (
                                <span style={{ marginLeft: '10px', fontSize: '14px' }}>
                                    {importFile.name}
                                </span>
                            )}
                        </div>
                        <button 
                            className="btn" 
                            onClick={downloadModeloPlanilha} 
                            disabled={importLoading}
                            style={{ 
                                marginLeft: '10px',
                                fontSize: '14px',
                                padding: '8px 15px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                fontWeight: 'bold',
                                border: 'none',
                                borderRadius: '4px'
                            }}
                        >
                            Baixar modelo
                        </button>
                    </div>

                    <div style={{ 
                        backgroundColor: '#f8f9fa', 
                        padding: '12px', 
                        borderRadius: '4px',
                        marginBottom: '15px',
                        fontSize: '13px'
                    }}>
                        <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>Colunas necessárias:</p>
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(3, 1fr)', 
                            gap: '5px'
                        }}>
                            <span>• Código de ref.</span>
                            <span>• Documento</span>
                            <span>• Nome</span>
                            <span>• Valor</span>
                            <span>• Limite (dias)</span>
                            <span>• Vencimento</span>
                            <span>• Aceita Pix</span>
                            <span>• Aceita Cartão</span>
                            <span>• Aceita Boleto</span>
                        </div>
                    </div>
                    
                    {importValidationErrors.length > 0 && (
                        <div style={{ 
                            marginBottom: '15px', 
                            backgroundColor: '#f8d7da',
                            color: '#721c24',
                            padding: '10px',
                            borderRadius: '4px',
                            fontSize: '13px'
                        }}>
                            <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>Erros de validação:</p>
                            <ul style={{ margin: '0 0 0 15px', padding: '0' }}>
                                {importValidationErrors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                        <button 
                            className="btn" 
                            onClick={() => setShowImportModal(false)} 
                            disabled={importLoading}
                            style={{ marginRight: '10px' }}
                        >
                            Cancelar
                        </button>
                        <button 
                            className="btn" 
                            onClick={handleImportSubmit} 
                            disabled={!importFile || importLoading}
                            style={{ 
                                backgroundColor: '#4cd964', 
                                color: 'white',
                                fontWeight: 'bold',
                                padding: '8px 20px',
                                borderRadius: '4px',
                                border: 'none'
                            }}
                        >
                            {importLoading ? 'Processando...' : 'Importar'}
                        </button>
                    </div>
                </div>
            </Modal>
            
            {/* Modal de sucesso da importação */}
            <Modal
                show={importSuccess}
                title="Importação Concluída com Sucesso"
                onClose={() => {
                    setImportSuccess(false);
                    setShowImportModal(false);
                }}
                largura="500px"
            >
                <div style={{ padding: '20px' }}>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        marginBottom: '20px' 
                    }}>
                        <div style={{ 
                            backgroundColor: '#4cd964', 
                            borderRadius: '50%', 
                            width: '60px', 
                            height: '60px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center' 
                        }}>
                            <span style={{ 
                                color: 'white', 
                                fontSize: '30px', 
                                fontWeight: 'bold' 
                            }}>&#10003;</span>
                        </div>
                    </div>
                    
                    <h3 style={{ 
                        textAlign: 'center', 
                        marginBottom: '20px', 
                        color: '#333' 
                    }}>
                        Importação concluída com sucesso!
                    </h3>
                    
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(2, 1fr)', 
                        gap: '15px', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '8px', 
                        padding: '20px', 
                        marginBottom: '20px' 
                    }}>
                        {importResults && (
                            <>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
                                        {importResults.total}
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#666' }}>Total Processado</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                                        {importResults.inseridas}
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#666' }}>Novas Cobranças</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fd7e14' }}>
                                        {importResults.atualizadas}
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#666' }}>Atualizadas</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#6c757d' }}>
                                        {importResults.ignoradas}
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#666' }}>Ignoradas</div>
                                </div>
                            </>
                        )}
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <button 
                            className="btn" 
                            onClick={() => {
                                setImportSuccess(false);
                                setShowImportModal(false);
                            }}
                            style={{ 
                                backgroundColor: '#007bff', 
                                color: 'white',
                                fontWeight: 'bold',
                                padding: '10px 25px',
                                borderRadius: '4px',
                                border: 'none'
                            }}
                        >
                            Fechar
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
}
